/**
 * Integrytas Group — Theme tokens & runtime application.
 *
 * A Theme is a generic, application-agnostic bundle: a palette (the 31 semantic
 * color tokens that back every Tailwind utility via the --c-* channel vars), a
 * font family, and the density / font-size presentation settings. applyTheme()
 * writes the palette to documentElement as inline CSS vars (which beat the
 * index.css :root and [data-theme="dark"] defaults), so switching a theme reskins
 * the WHOLE app — shell, sidebar, login, every page — with no code changes.
 *
 * This is the platform mechanism (CLAUDE §11 / §16): derived apps re-theme via a
 * theme row, never by editing tailwind.config or branding.ts.
 */

export type ThemeMode = 'light' | 'dark';
export type Density = 'compact' | 'comfortable';
export type FontSize = 'small' | 'medium' | 'large';
export type ThemeScope = 'builtin' | 'org' | 'user';

/** The canonical color tokens. Each maps to --color-<token> (hex, for direct CSS)
 *  and --c-<token> (R G B channels, for the Tailwind rgb(var(--c-x)/<alpha>) tokens). */
export const THEME_TOKENS = [
  'primary', 'primary-hover', 'primary-soft',
  'surface', 'secondary-bg', 'surface-alt',
  'border', 'border-soft', 'border-strong',
  'text', 'text-muted', 'text-soft',
  'sidebar-bg', 'sidebar-bg-alt', 'sidebar-text', 'sidebar-muted',
  'watermark', 'accent', 'warn',
  'success', 'success-soft', 'success-border',
  'warning', 'warning-soft', 'warning-border',
  'danger', 'danger-soft', 'danger-border',
  'info', 'info-soft', 'info-border',
] as const;

export type ThemeToken = (typeof THEME_TOKENS)[number];
export type Palette = Record<ThemeToken, string>;

/** Token groups for the designer UI (label + member tokens, with a friendly name). */
export const TOKEN_GROUPS: { group: string; tokens: { key: ThemeToken; label: string }[] }[] = [
  { group: 'Brand', tokens: [
    { key: 'primary', label: 'Primary' },
    { key: 'primary-hover', label: 'Primary (hover)' },
    { key: 'primary-soft', label: 'Primary (soft wash)' },
    { key: 'accent', label: 'Accent' },
  ] },
  { group: 'Sidebar', tokens: [
    { key: 'sidebar-bg', label: 'Sidebar background' },
    { key: 'sidebar-bg-alt', label: 'Sidebar footer' },
    { key: 'sidebar-text', label: 'Sidebar text' },
    { key: 'sidebar-muted', label: 'Sidebar muted' },
  ] },
  { group: 'Surfaces', tokens: [
    { key: 'surface', label: 'Surface (cards/grids)' },
    { key: 'secondary-bg', label: 'Workspace background' },
    { key: 'surface-alt', label: 'Subtle band / zebra' },
    { key: 'watermark', label: 'Watermark' },
  ] },
  { group: 'Borders', tokens: [
    { key: 'border', label: 'Border' },
    { key: 'border-soft', label: 'Border (soft)' },
    { key: 'border-strong', label: 'Border (strong)' },
  ] },
  { group: 'Text', tokens: [
    { key: 'text', label: 'Body text' },
    { key: 'text-soft', label: 'Text (soft)' },
    { key: 'text-muted', label: 'Text (muted)' },
  ] },
  { group: 'Status', tokens: [
    { key: 'success', label: 'Success' },
    { key: 'success-soft', label: 'Success (soft)' },
    { key: 'success-border', label: 'Success (border)' },
    { key: 'warning', label: 'Warning' },
    { key: 'warning-soft', label: 'Warning (soft)' },
    { key: 'warning-border', label: 'Warning (border)' },
    { key: 'danger', label: 'Danger' },
    { key: 'danger-soft', label: 'Danger (soft)' },
    { key: 'danger-border', label: 'Danger (border)' },
    { key: 'info', label: 'Info' },
    { key: 'info-soft', label: 'Info (soft)' },
    { key: 'info-border', label: 'Info (border)' },
    { key: 'warn', label: 'Warn (legacy amber)' },
  ] },
];

export interface ThemeDefinition {
  code: string;
  name: string;
  scope: ThemeScope;
  mode: ThemeMode;
  colors: Palette;
  font: string;
  density: Density;
  fontSize: FontSize;
  owner_user_id?: number | null;
  /** present for org/user themes loaded from csm_theme */
  theme_id?: number;
}

/** Curated font stacks offered in the designer. Roboto/Inter are @imported in index.css. */
export const FONT_STACKS: { label: string; value: string }[] = [
  { label: 'System (default)', value: "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" },
  { label: 'Roboto', value: "'Roboto', system-ui, sans-serif" },
  { label: 'Inter', value: "'Inter', system-ui, sans-serif" },
  { label: 'Georgia (serif)', value: "Georgia, 'Times New Roman', serif" },
  { label: 'Monospace', value: "ui-monospace, 'SF Mono', 'Roboto Mono', Menlo, monospace" },
];

const HEX3 = /^#?([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/;
const HEX6 = /^#?([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/;

/** '#00529B' -> '0 82 155' (Tailwind channel form). Returns null for invalid input. */
export function hexToChannels(hex: string): string | null {
  const h = (hex ?? '').trim();
  let m = HEX6.exec(h);
  if (m) return `${parseInt(m[1], 16)} ${parseInt(m[2], 16)} ${parseInt(m[3], 16)}`;
  m = HEX3.exec(h);
  if (m) return `${parseInt(m[1] + m[1], 16)} ${parseInt(m[2] + m[2], 16)} ${parseInt(m[3] + m[3], 16)}`;
  return null;
}

export function isValidHex(hex: string): boolean {
  return hexToChannels(hex) !== null;
}

/** Apply a theme's palette + font + mode to the live document. Inline vars on
 *  documentElement win over every stylesheet default, so this reskins the whole
 *  app immediately. Missing tokens fall back to the supplied base palette so
 *  partial themes never unset a var. Density / font-size are intentionally NOT set
 *  here — they stay owned by the presentation hook (the single writer of those
 *  data-attributes); applyDensityFontSize() handles them. */
export function applyTheme(def: ThemeDefinition, basePalette: Palette): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  for (const token of THEME_TOKENS) {
    const hex = def.colors[token] ?? basePalette[token];
    if (!hex) continue;
    root.style.setProperty(`--color-${token}`, hex);
    const ch = hexToChannels(hex);
    if (ch) root.style.setProperty(`--c-${token}`, ch);
  }
  if (def.font) root.style.setProperty('--font-sans', def.font);
  root.setAttribute('data-theme', def.mode);
}

export function applyDensityFontSize(density: Density, fontSize: FontSize): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.setAttribute('data-density', density);
  root.setAttribute('data-font-size', fontSize);
}
